import { historicalGraphWindow } from "./historicalGraphWindow";
import { getTigerResource, tigerFormat } from "./tigerResource";
import { TIGER_TO_UNIX_OFFSET_SECS, formatTigerDateTime } from "./tigerTime";
import { getViewRunState, setViewMode } from "./viewMode";

/** Global event (re-broadcast by the Rust `broadcast_event` command) asking the
 *  main window to open the Load Data dialog seeded for a replay. A frontend
 *  emit() does not reliably reach other webviews in Tauri v2; app.emit() does. */
export const LOAD_DATA_REPLAY_EVENT = "load-data-replay";

/** Turbine + window (Tiger seconds, 1901 epoch) to seed the Load Data dialog. */
export interface ReplayFromRequest {
  config: string;
  start: number;
  end: number;
}

/** Diagnostics "Replay From" half-window: 4 minutes each side of the diag time
 *  (C++ diagnostics_dialog.cpp REPLAY_FROM_MENU_SIZE_IN_MINS = 4). */
export const DIAG_REPLAY_HALF_WINDOW_SECS = 4 * 60;

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Replay window (Tiger seconds) for a right-click on a live trend, matching the
 *  C++ replay-from-trend math (graph_dialog.cpp, via historicalGraphWindow). The
 *  cursor time arrives as Unix seconds (uPlot x value); the sample rate sizes the
 *  window. Both bounds are shifted to Tiger seconds. */
export function trendReplayWindow(
  cursorUnixSecs: number,
  sampleRateSecs: number,
): { start: number; end: number } {
  const { start, end } = historicalGraphWindow(cursorUnixSecs, sampleRateSecs);
  return {
    start: start + TIGER_TO_UNIX_OFFSET_SECS,
    end: end + TIGER_TO_UNIX_OFFSET_SECS,
  };
}

/** Replay window (Tiger seconds) for a diagnostic: 4 min before to 4 min after
 *  the diagnostic time (already Tiger seconds). */
export function diagReplayWindow(diagTigerSecs: number): { start: number; end: number } {
  return {
    start: diagTigerSecs - DIAG_REPLAY_HALF_WINDOW_SECS,
    end: diagTigerSecs + DIAG_REPLAY_HALF_WINDOW_SECS,
  };
}

/** Diagnostics menu label for a Tiger-second time, e.g.
 *  "Replay From 22:13:20 14/11/2023" (C++ TIGER_DIAG_REPLAY_FROM). */
export function replayFromLabel(tigerSecs: number): string {
  return tigerFormat(getTigerResource("TIGER_DIAG_REPLAY_FROM"), formatTigerDateTime(tigerSecs));
}

/** Graph/trend menu label for a cursor time given in Unix seconds (C++
 *  TIGER_GRAPH_REPLAY_FROM — same text, separate resource, as in the C++). */
export function trendReplayLabel(cursorUnixSecs: number): string {
  const tigerSecs = Math.round(cursorUnixSecs) + TIGER_TO_UNIX_OFFSET_SECS;
  return tigerFormat(getTigerResource("TIGER_GRAPH_REPLAY_FROM"), formatTigerDateTime(tigerSecs));
}

/** Ask the main window to open the Load Data dialog seeded with this turbine +
 *  window. Routed through the Rust broadcast_event command (global emit) so it
 *  reaches the main window from a tool window. No-op outside Tauri. */
export async function requestReplayFrom(config: string, start: number, end: number): Promise<void> {
  if (!isTauri()) return;
  const { invoke } = await import("@tauri-apps/api/core");
  const payload: ReplayFromRequest = { config, start, end };
  await invoke("broadcast_event", { event: LOAD_DATA_REPLAY_EVENT, payload });
}

/** Web "Replay From": a tool window (graph / diagnostics) is its own browser tab
 *  and can't open — let alone focus — the main window's Load Data dialog (the
 *  desktop path), so the action is handled in-tab. Mirrors App.tsx's desktop
 *  listener: switch the session to Replay first (only if still live) so the views
 *  stop following live data, then hand the seed back so the caller can open its
 *  own seeded Load Data dialog. Returns the seed for that dialog. */
export async function prepareWebReplayFrom(
  config: string,
  start: number,
  end: number,
): Promise<ReplayFromRequest> {
  if ((await getViewRunState()) === "live") {
    await setViewMode(false).catch((e) => console.error("switch to replay failed:", e));
  }
  return { config, start, end };
}
